import { useState } from "react";
import axios from "axios";
import { Feather, Loader2, Users, Lock, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [key, setKey] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e?.preventDefault();
    if (!key.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/leads`, {
        headers: { "X-Admin-Key": key.trim() },
      });
      setLeads(res.data);
    } catch (err) {
      setLeads(null);
      setError(
        err?.response?.status === 401
          ? "Chave de acesso incorreta."
          : "Não foi possível carregar os leads. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-[#08080a] font-sans text-[#f4f1ea]">
      <header className="border-b border-[#d4af37]/15">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="admin-brand" className="flex items-center gap-2.5">
            <Feather className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
            <span className="font-serif text-xl tracking-[0.28em]">ESTÓICO</span>
          </a>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]/70">
            Área do autor
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight">
          Leads <span className="text-gold-gradient italic">capturados</span>
        </h1>
        <p className="mt-3 text-sm sm:text-base text-zinc-400 max-w-xl">
          E-mails de visitantes que pediram o capítulo gratuito na página de vendas.
        </p>

        <form data-testid="admin-key-form" onSubmit={load} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
          <div className="relative flex-1">
            <Lock className="w-4 h-4 text-zinc-500 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Chave de acesso"
              autoComplete="off"
              className="w-full bg-[#14141a] border border-[#d4af37]/20 rounded-full pl-12 pr-6 py-3.5 text-sm placeholder:text-zinc-600 outline-none focus:border-[#d4af37]/60 transition-colors"
            />
          </div>
          <button
            data-testid="admin-load-button"
            type="submit"
            disabled={loading || !key.trim()}
            className="inline-flex items-center justify-center gap-2 bg-gold-gradient text-[#08080a] font-bold text-sm px-8 py-3.5 rounded-full transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            Ver leads
          </button>
        </form>

        {error && (
          <p data-testid="admin-error" role="alert" className="mt-4 text-sm text-red-400">
            {error}
          </p>
        )}

        {leads && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <p data-testid="admin-leads-count" className="font-mono text-xs uppercase tracking-[0.25em] text-[#d4af37]">
                {leads.length} {leads.length === 1 ? "lead" : "leads"}
              </p>
              <button
                data-testid="admin-refresh-button"
                onClick={load}
                className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#f5d77f] transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Atualizar
              </button>
            </div>
            <div className="rounded-2xl border border-[#d4af37]/15 overflow-hidden">
              <table data-testid="admin-leads-table" className="w-full text-sm">
                <thead>
                  <tr className="bg-[#14141a] text-left font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    <th className="px-6 py-4">#</th>
                    <th className="px-6 py-4">E-mail</th>
                    <th className="px-6 py-4">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <tr
                      key={lead.id}
                      data-testid={`admin-lead-row-${i + 1}`}
                      className="border-t border-white/[0.05] hover:bg-[#14141a]/60 transition-colors"
                    >
                      <td className="px-6 py-4 text-zinc-600">{i + 1}</td>
                      <td className="px-6 py-4 text-[#f4f1ea]">{lead.email}</td>
                      <td className="px-6 py-4 text-zinc-400">
                        {new Date(lead.created_at).toLocaleString("pt-BR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leads.length === 0 && (
                <p className="px-6 py-10 text-center text-sm text-zinc-500">
                  Nenhum lead capturado ainda.
                </p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
